import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { default as HeaderDS } from 'design-system/src/components/organisms/Header';
import HeaderAlert from 'design-system/src/components/organisms/HeaderAlert';
import { graphql } from 'gatsby';

import { useTranslate } from 'gatsby-theme-soluble-source';
import { navigate } from '@soluto-design/link';

import useModal from '../../hooks/useModal';
import { formatLink } from '../../utils/link';
import { getImage } from '../../utils/media';
import { formatResource } from '../../utils/resources';

function Header({ data, layoutProps, ...props }) {
    const {
        title,
        options,
        leftItems,
        rightItems,
        menuItems,
        mobile,
        mobileBottom,
    } = data;
    const [showAlert, setShowAlert] = useState(false);

    const { openModal } = useModal();

    const _ = useTranslate();

    const {
        highlightedPostTitle,
        highlightedPostCtaLabel,
        closeModalLabel,
        videoAlternativeCtaLabel,
        videoDescription,
        videoUrl,
        videoThumbnail,
        videoAutoplay,
        menuBanner: banner,
    } = options ?? {};

    const highlightedPost = options?.highlightedPost;

    const { alert } = layoutProps ?? {};

    const video = useMemo(
        () =>
            videoUrl?.videoId
                ? {
                      videoId: videoUrl?.videoId ?? null,
                      description: videoDescription ?? null,
                      cta: {
                          ...options?.videoCta,
                      },
                      alternativeCtaLabel: videoAlternativeCtaLabel,
                      thumbnail: getImage(videoThumbnail) ?? null,
                      closeModalLabel,
                  }
                : null,
        [
            videoUrl,
            videoDescription,
            videoAlternativeCtaLabel,
            options,
            closeModalLabel,
            videoThumbnail,
        ],
    );

    const thisMenu = useMemo(() => {
        return menuItems?.nodes?.map(formatLink);
    }, [menuItems]);

    const thisRightItems = useMemo(() => {
        return rightItems?.items?.map((item, i) => {
            const isVideoBtn = item?.href?.indexOf('#open-video') !== -1;

            return {
                ...formatLink(item),
                variant: 'secondary',
                ...(isVideoBtn && {
                    variant: 'primary',
                    onClick: (e) => {
                        e?.preventDefault();
                        openModal(videoAutoplay, video);
                    },
                    leftIcon: 'play--filled-on',
                }),
            };
        });
    }, [video, videoAutoplay, rightItems, openModal]);

    const thisModalBottom = useMemo(() => {
        return mobileBottom?.items?.map((item, i) => {
            const isVideoBtn = item?.href?.indexOf('#open-video') !== -1;

            return {
                ...formatLink(item),
                variant: 'secondary',
                ...(isVideoBtn && {
                    variant: 'primary',
                    onClick: (e) => {
                        e?.preventDefault();
                        openModal(videoAutoplay, video);
                    },
                    leftIcon: 'play--filled-on',
                }),
            };
        });
    }, [video, videoAutoplay, mobileBottom, openModal]);

    const thisLeftItems = useMemo(() => {
        return leftItems?.items?.map(formatLink);
    }, [leftItems]);

    const mobileItems = useMemo(() => {
        return mobile?.items?.map(formatLink);
    }, [mobile]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (layoutProps && layoutProps.alert) {
            const previouslyHidden =
                window.sessionStorage.getItem('headerAlert') === 'hide';

            if (!previouslyHidden) {
                setShowAlert(true);
            }

            document.body.dataset.alert = !previouslyHidden ? 'true' : 'false';
        }
    }, [alert, layoutProps]);

    const closeAlert = useCallback(() => {
        setShowAlert(false);
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('headerAlert', 'hide');
            document.body.dataset.alert = 'false';
        }
    }, []);

    return (
        <>
            {showAlert ? (
                <HeaderAlert onClose={closeAlert}>
                    {layoutProps.alert}
                </HeaderAlert>
            ) : null}
            <HeaderDS
                leftItems={thisLeftItems}
                rightItems={thisRightItems}
                mobileItems={mobileItems}
                mobileBottomItems={thisModalBottom}
                logoAlt={title}
                banner={banner}
                menu={thisMenu}
                withBanner={!!showAlert}
                highlightedPost={{
                    ...formatResource(highlightedPost, 'DatoCmsBlogPost', _),
                    cta: {
                        label: highlightedPostCtaLabel,
                        href: highlightedPost?.href,
                    },
                    onClick: (ev) => {
                        ev?.preventDefault();
                        navigate(highlightedPost?.href);
                    },
                }}
                highlightedPostTitle={{
                    label: highlightedPostTitle?.children,
                    href: highlightedPostTitle?.href,
                }}
                video={video}
                {...layoutProps}
            />
        </>
    );
}

export default Header;

export const query = graphql`
    fragment HeaderLink on DatoCmsHeader {
        label
        children: label
        title: label
        href
        openInNewWindow
    }
    query Header($language: String) {
        options: datoCmsOption {
            menuBanner
            videoUrl {
                ...YoutubeVideo
            }
            videoDescription
            videoCta {
                ...Button
            }
            videoAlternativeCtaLabel
            videoAutoplay
            videoThumbnail {
                ...SourceMedia
                gatsbyImageData(placeholder: BLURRED, height: 1080, width: 1920)
            }
            closeModalLabel
            highlightedPostTitle {
                ...Button
            }
            highlightedPostCtaLabel
            highlightedPost {
                ...DatoCmsBlogPostItem
            }
        }
        menuItems: allDatoCmsHeader(
            filter: {
                parent: { id: { eq: null } }
                locale: { eq: $language }
                label: { in: ["_1", "_2", "_3"] }
            }
            sort: { position: ASC }
        ) {
            nodes {
                ...HeaderLink
                items: treeChildren {
                    ...HeaderLink
                    items: treeChildren {
                        ...HeaderLink
                    }
                }
            }
        }
        rightItems: datoCmsHeader(
            label: { eq: "_right" }
            parent: { id: { eq: null } }
            locale: { eq: $language }
        ) {
            ...HeaderLink
            items: treeChildren {
                ...HeaderLink
            }
        }
        leftItems: datoCmsHeader(
            label: { eq: "_left" }
            parent: { id: { eq: null } }
            locale: { eq: $language }
        ) {
            ...HeaderLink
            items: treeChildren {
                ...HeaderLink
            }
        }
        mobile: datoCmsHeader(
            label: { eq: "_mobile" }
            parent: { id: { eq: null } }
            locale: { eq: $language }
        ) {
            ...HeaderLink
            items: treeChildren {
                ...HeaderLink
            }
        }
        mobileBottom: datoCmsHeader(
            label: { eq: "_mobile_bottom" }
            parent: { id: { eq: null } }
            locale: { eq: $language }
        ) {
            ...HeaderLink
            items: treeChildren {
                ...HeaderLink
            }
        }
    }
`;
